import React, { useEffect, useState } from 'react';
import Layout from '../components/Layout';
import PostCard from '../components/PostCard';
import CommentDrawer from '../components/CommentDrawer';
import api from '../lib/api';
import { Sparkles, Flame } from 'lucide-react';
import { motion } from 'framer-motion';

export const Discover = () => {
  const [trending, setTrending] = useState([]);
  const [recs, setRecs] = useState({ summary: '', posts: [] });
  const [genres, setGenres] = useState([]);
  const [selectedGenre, setSelectedGenre] = useState(null);
  const [genrePosts, setGenrePosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeComment, setActiveComment] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [t, g, r] = await Promise.all([
          api.get('/posts/trending'),
          api.get('/discover/genres'),
          api.get('/discover/recommendations'),
        ]);
        setTrending(t.data);
        setGenres(g.data);
        setRecs(r.data);
      } catch {}
      setLoading(false);
    })();
  }, []);

  const pickGenre = async (g) => {
    setSelectedGenre(g);
    setGenrePosts([]);
    if (!g) return;
    try {
      const { data } = await api.get(`/posts/by-genre/${encodeURIComponent(g)}`);
      setGenrePosts(data);
    } catch {}
  };

  return (
    <Layout>
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="mb-8"
      >
        <div className="text-xs tracking-[0.2em] uppercase font-bold text-[#FFB000] mb-2">Discover</div>
        <h1 className="font-heading font-black text-white text-4xl md:text-5xl tracking-tighter">
          New sounds. New crews.
        </h1>
      </motion.div>

      {/* AI Recs */}
      <div className="rounded-3xl p-6 mb-10 border border-[#FFB000]/20 bg-gradient-to-br from-[#FFB000]/10 via-[#0F0F11] to-[#0F0F11] relative overflow-hidden" data-testid="ai-recommendations">
        <div className="absolute top-0 right-0 w-64 h-64 bg-[#FFB000]/10 rounded-full blur-3xl -z-0" />
        <div className="relative z-10">
          <div className="flex items-center gap-2 mb-3">
            <Sparkles size={18} className="text-[#FFB000]" />
            <span className="text-xs tracking-[0.2em] uppercase font-bold text-[#FFB000]">AI Curated for you</span>
          </div>
          <p className="text-white text-lg md:text-xl leading-relaxed font-heading max-w-2xl mb-4" data-testid="ai-summary">
            {loading ? 'Tuning into your vibes...' : (recs.summary || 'Follow some DJs and like mixes to unlock personalized recommendations.')}
          </p>
        </div>
      </div>

      {/* Genres */}
      <div className="mb-10">
        <h2 className="font-heading font-bold text-white text-xl mb-4">Browse by genre</h2>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => pickGenre(null)}
            className={`px-4 py-2 rounded-full text-sm font-bold font-heading transition-all ${
              !selectedGenre
                ? 'bg-[#FFB000] text-black'
                : 'bg-[#1A1A1D] text-[#A1A1AA] hover:text-white border border-white/5'
            }`}
            data-testid="genre-all"
          >
            All
          </button>
          {genres.map(g => (
            <button
              key={g}
              onClick={() => pickGenre(g)}
              className={`px-4 py-2 rounded-full text-sm font-bold font-heading transition-all ${
                selectedGenre === g
                  ? 'bg-[#FFB000] text-black'
                  : 'bg-[#1A1A1D] text-[#A1A1AA] hover:text-white border border-white/5'
              }`}
              data-testid={`genre-${g}`}
            >
              {g}
            </button>
          ))}
        </div>
      </div>

      {/* Content: genre specific OR trending+recs */}
      {selectedGenre ? (
        <div>
          <h2 className="font-heading font-bold text-white text-2xl mb-4">#{selectedGenre}</h2>
          {genrePosts.length === 0 ? (
            <p className="text-[#A1A1AA]">No mixes yet in this genre.</p>
          ) : (
            <div className="space-y-6">
              {genrePosts.map(p => (
                <PostCard key={p.id} post={p} onCommentOpen={setActiveComment} />
              ))}
            </div>
          )}
        </div>
      ) : (
        <>
          {recs.posts?.length > 0 && (
            <div className="mb-10">
              <h2 className="font-heading font-bold text-white text-2xl mb-4 flex items-center gap-2">
                <Sparkles size={20} className="text-[#FFB000]" />
                Picks for you
              </h2>
              <div className="space-y-6">
                {recs.posts.slice(0, 5).map(p => (
                  <PostCard key={p.id} post={p} onCommentOpen={setActiveComment} />
                ))}
              </div>
            </div>
          )}

          <div>
            <h2 className="font-heading font-bold text-white text-2xl mb-4 flex items-center gap-2">
              <Flame size={20} className="text-[#FFB000]" />
              Trending
            </h2>
            {trending.length === 0 ? (
              <p className="text-[#A1A1AA]">No trending mixes yet.</p>
            ) : (
              <div className="space-y-6">
                {trending.map(p => (
                  <PostCard key={p.id} post={p} onCommentOpen={setActiveComment} />
                ))}
              </div>
            )}
          </div>
        </>
      )}
      <CommentDrawer post={activeComment} onClose={() => setActiveComment(null)} />
    </Layout>
  );
};

export default Discover;
